import moment from "moment";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, controlToastState, SpinningLoader } from "risa-oasis-ui_v2";
import { v4 as uuidv4 } from "uuid";
import { FirestoreService } from "../../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../api/firebase/references";
import {
  StatusLogData,
  StatusLogDataWithId,
} from "../../../../data-model/statusLogModel";
import { RootState } from "../../../../redux/store/store";
import { BorderCircle } from "../../../../svg/border-circle";
import { CrossIcon } from "../../../../svg/cross-icon";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { LogEvenets } from "./sidebarComponents/LogEvenets";

interface StatusLogProps {
  onClose: () => void;
  orderId: string;
  onSideModalClose?: () => void;
}

const StatusLog = (props: StatusLogProps) => {
  const [comments, setComments] = useState<string>("");
  const [selectedFollowUpType, setSelectedFollowUpType] = useState<{
    id: string;
    label: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState<string>("log_event");
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState<boolean>(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(true);

  // Redux selectors
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const { data: providerDetails } = useSelector(
    (state: RootState) => state.providerDetails,
  );

  const [templates, setTemplates] = useState<string[]>([]);
  const [historyData, setHistoryData] = useState<StatusLogDataWithId[]>([]);

  const getHistoryData = async () => {
    try {
      setIsLoadingHistory(true);
      const response =
        await FirestoreService.getAllDocuments<StatusLogDataWithId>(
          FirestoreCollectionReference.medicalPaOrdersComments(props.orderId),
        );

      const sortedData = response.sort(
        (a, b) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      );

      setHistoryData(sortedData);
    } catch (error) {
      console.error("Error fetching history data:", error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  useEffect(() => {
    getHistoryData();
  }, []);

  // Tab elements for switching between screens
  const tabElements = [
    { id: "log_event", label: "Log Event" },
    { id: "history", label: "History" },
  ];

  const getTemplates = async () => {
    try {
      setIsLoadingTemplates(true);
      const organization = getOrgIdForFetchExternalWorklist();
      const response = await FirestoreService.getAllDocuments<any>(
        FirestoreCollectionReference.followUpType(organization),
      );

      const templatesData = response.find(
        (item: any) => item.id === "templates",
      )?.data;

      if (templatesData && Array.isArray(templatesData)) {
        setTemplates(templatesData as string[]);
      }
    } catch (error) {
      console.error("Error fetching follow-up types:", error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  useEffect(() => {
    getTemplates();
  }, []);

  const handleSaveClick = async () => {
    if (!selectedFollowUpType || !comments.trim()) {
      return;
    }

    if (!user?.email || !providerDetails) {
      return;
    }

    setIsSaving(true);

    try {
      const statusLogData: StatusLogData = {
        created_at: new Date().toISOString(),
        provider_name:
          `${providerDetails.FirstName || ""} ${providerDetails.LastName || ""}`.trim() ||
          "Unknown Provider",
        provider_email: user.email,
        follow_up_type_id: selectedFollowUpType.id,
        follow_up_type_label: selectedFollowUpType.label,
        comments: comments.trim(),
      };

      const statusLogEntryId = uuidv4();

      await FirestoreService.setDocument(
        FirestoreCollectionReference.medicalPaOrdersComments(props.orderId),
        statusLogEntryId,
        statusLogData,
      );

      await FirestoreService.updateDocument(
        FirestoreCollectionReference.medicalPaOrders(),
        props.orderId,
        {
          "timestamps.date_of_last_checked": moment().format(
            "YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]",
          ),
        },
      );

      controlToastState("status-log-saved");
      props.onSideModalClose?.();
      props.onClose();
    } catch (error) {
      controlToastState("status-log-save-failure");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="status-log--main-container flex h-full flex-col">
      <div className="status-log--header mr-auto flex w-full flex-row justify-between border-b-1 border-primaryGray-14 p-4 text-h11 font-bold text-primaryGray-1">
        <div>Status Logs</div>
        <CrossIcon
          onClick={() => {
            props.onClose();
          }}
        />
      </div>

      {/* Tab Navigation */}
      <div className="status-log--tabs flex flex-row border-b border-primaryGray-14 pt-2">
        {tabElements.map((tab) => (
          <div
            key={tab.id}
            className={`status-log--tab cursor-pointer px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? "border-b-2 border-tertiaryBlue-4 bg-tertiaryBlue-13 font-bold text-tertiaryBlue-4"
                : "text-primaryGray-6 hover:border-transparent"
            }`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      {/* Log Event Screen */}
      <div className="status-log--body flex h-full w-full flex-col gap-4 overflow-y-auto p-4">
        {activeTab === "log_event" && (
          <>
            {isLoadingTemplates ? (
              <div className="flex h-full items-center justify-center py-8">
                <div className="text-center">
                  <SpinningLoader />
                  <p className="mt-2 text-gray-600">Loading templates...</p>
                </div>
              </div>
            ) : (
              <LogEvenets
                templates={templates}
                setComments={setComments}
                setSelectedFollowUpType={(followUpType) =>
                  setSelectedFollowUpType(followUpType)
                }
              />
            )}
          </>
        )}

        {/* History Screen */}
        {activeTab === "history" && (
          <div className="status-log--history--container flex h-full flex-col gap-2">
            {isLoadingHistory ? (
              <div className="flex h-full items-center justify-center py-8">
                <div className="text-center">
                  <SpinningLoader />
                  <p className="mt-2 text-gray-600">Loading history...</p>
                </div>
              </div>
            ) : historyData.length === 0 ? (
              <div className="flex h-32 items-center justify-center text-primaryGray-6">
                No history data available
              </div>
            ) : (
              historyData.map((entry, index) => {
                const createdDate = new Date(entry.created_at);
                const formattedTime = createdDate.toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: true,
                });
                const formattedDate = createdDate.toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "2-digit",
                });

                return (
                  <div key={entry.id} className="flex w-full flex-row gap-2">
                    <div className="flex w-10 flex-col items-center">
                      <BorderCircle />
                      {index < historyData.length - 1 && (
                        <div
                          className="mt-3 flex h-full flex-1 items-start justify-center border-r border-dashed border-primaryGray-14"
                          style={{
                            borderRightStyle: "dashed",
                            borderRightWidth: "1px",
                            borderImage:
                              "repeating-linear-gradient(to bottom, currentColor 0, currentColor 8px, transparent 8px, transparent 16px) 1",
                            borderRightColor: "currentColor",
                          }}
                        ></div>
                      )}
                    </div>
                    <div className="flex w-full flex-col gap-2">
                      <div className="flex flex-row items-start gap-1 text-tiny font-normal text-primaryGray-6">
                        <div>{formattedTime}</div>
                        <div>|</div>
                        <div>{formattedDate}</div>
                      </div>
                      <div>
                        {entry.provider_name} has{" "}
                        <span className="font-semibold">
                          {entry.follow_up_type_label}
                        </span>
                      </div>
                      <div className="status-log--body--follow-up-type--templates bg- flex w-full flex-col gap-2 rounded rounded-s bg-primaryGray-16 p-3">
                        <div className="w-full text-wrap text-primaryGray-6">
                          {entry.comments}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="status-log--footer mt-auto flex w-full flex-row items-end justify-end gap-3 border-t border-primaryGray-15 p-4">
        <Button
          buttonType="secondary"
          size="medium"
          onClick={props.onClose}
          disabled={false}
        >
          {activeTab === "history" ? "Close" : "Cancel"}
        </Button>
        {activeTab === "log_event" && (
          <Button
            buttonType="primary"
            size="medium"
            onClick={handleSaveClick}
            disabled={
              isSaving ||
              isLoadingTemplates ||
              !selectedFollowUpType ||
              !comments.trim()
            }
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        )}
      </div>
    </div>
  );
};

export default StatusLog;
